// @ts-check
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Hook for infinite-scroll pagination.
 *
 * @param {Function} fetchFn - async (page, limit) => { items, total, pages }
 * @param {Object}   opts
 * @param {number}   [opts.limit=50]       - items per page
 * @param {number}   [opts.threshold=0.8]  - scroll % that triggers next fetch
 * @param {Array}    [opts.deps=[]]        - extra deps that reset the list (e.g. filters)
 *
 * @returns {{ items, loading, hasMore, reset, sentinelRef }}
 *   - items:       accumulated array
 *   - loading:     true while a page is being fetched
 *   - hasMore:     false when last page reached
 *   - reset:       call to clear items & restart from page 1
 *   - sentinelRef: attach to a div at the bottom of your list
 */
export default function useInfiniteScroll(fetchFn, { limit = 50, threshold = 0.8, deps = [] } = {}) {
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const sentinelRef = useRef(null);
  const fetchingRef = useRef(false);
  const pageRef = useRef(1);

  const loadPage = useCallback(
    async (pageNum) => {
      if (fetchingRef.current) return;
      fetchingRef.current = true;
      setLoading(true);

      try {
        const data = await fetchFn(pageNum, limit);
        const newItems = data.items || [];

        setItems((prev) => (pageNum === 1 ? newItems : [...prev, ...newItems]));
        const more = pageNum < data.pages;
        setHasMore(more);
        pageRef.current = pageNum;
      } catch (err) {
        console.error('useInfiniteScroll fetch error:', err);
        // Stop retrying on error to prevent infinite loop
        setHasMore(false);
      } finally {
        setLoading(false);
        fetchingRef.current = false;
      }
    },
    [fetchFn, limit],
  );

  // Reset when deps change (filters, search, etc.)
  useEffect(() => {
    setItems([]);
    setHasMore(true);
    pageRef.current = 1;
    loadPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, loadPage]);

  // IntersectionObserver on the sentinel element
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !fetchingRef.current) {
          loadPage(pageRef.current + 1);
        }
      },
      { rootMargin: '200% 0px', threshold: 0 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadPage]);

  const reset = useCallback(() => {
    setItems([]);
    setHasMore(true);
    pageRef.current = 1;
    loadPage(1);
  }, [loadPage]);

  return { items, loading, hasMore, reset, sentinelRef };
}
